<script setup lang="ts">
  import type { IInput } from '@/components/ui/input/types'
  import tZodMessages from '@/utils/i18n/tZodMessages'

  const props = defineProps<IInput>()
  const [modelValue, modifiers] = defineModel<string>({
    set(value) {
      if (modifiers.trimallspaces) {
        return value.replace(/\s+/g, '')
      }

      if (modifiers.lowercase) {
        return value.toLowerCase()
      }

      return value
    }
  })

  const emit = defineEmits(['iconClick', 'blur'])

  const inputId = computed(() => `${props.name}_${props.inputKey}`)
  const errorId = computed(() => `${inputId.value}-error`)
  const loadingId = computed(() => `${inputId.value}-loading`)

  const describedBy = computed(() => {
    if (props.error) {
      return errorId.value
    }

    if (props.loading) {
      return loadingId.value
    }

    return undefined
  })

  const inputElement = ref<HTMLInputElement | null>(null)
  onMounted(() => {
    if (props.autofocus) {
      nextTick(() => {
        inputElement.value?.focus()
      })
    }
  })
</script>
<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="mb-2 flex items-center gap-1 text-bt-blue-dark text-subtitle-2"
      :class="{ 'sr-only': srOnlyLabel }"
    >
      {{ label }}
      <template v-if="optional"> ({{ $t('forms.optional') }}) </template>
      <template v-if="tooltip">
        <div class="flex items-center justify-center">
          <UiTooltip
            :text="tooltip"
            tooltip-style="white"
            hoverable
          >
            <Icon
              name="common/outline-info"
              size="sm"
            />
          </UiTooltip>
        </div>
      </template>
    </label>

    <div class="relative">
      <input
        :id="inputId"
        ref="inputElement"
        v-model="modelValue"
        :name="props.name"
        class="w-full rounded border border-border px-4 py-3 text-bt-blue-dark outline-bt-blue-ui text-body-1 disabled:bg-grey"
        :class="{ 'border-dark-error': error, 'pr-10': !!icon || loading }"
        :placeholder="placeholder"
        :disabled="disabled"
        :type="type"
        :maxlength="maxLength"
        :aria-busy="loading || undefined"
        :aria-describedby="describedBy"
        @blur="(event) => emit('blur', event)"
      />

      <LazyUiLoaderTiny
        v-if="loading"
        :id="loadingId"
        :text="$t('aria.loading')"
        sr-only-text
        size="md"
        class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-bt-green"
      />

      <Icon
        v-else-if="icon"
        :name="icon"
        size="md"
        class="absolute right-3 top-1/2 -translate-y-1/2"
        :class="iconClasses"
        :tabindex="canClickIcon ? 0 : -1"
        @click="canClickIcon && emit('iconClick')"
        @keydown.enter="canClickIcon && emit('iconClick')"
      />
    </div>

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0 transform -translate-y-2"
      enter-to-class="opacity-100 max-h-20 transform translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 max-h-20 transform translate-y-0"
      leave-to-class="opacity-0 max-h-0 transform -translate-y-2"
    >
      <div
        v-if="error"
        :id="errorId"
        :class="{ error }"
        class="mt-2 block text-dark-error text-body-3"
      >
        {{ $t(...tZodMessages(error)) }}
      </div>
    </Transition>
  </div>
</template>
